import { useState } from 'react';

import {
  type ContractKind,
  type OffCycleRule,
  type ReferenceDateRule,
  type SubmissionCadence,
  type InsurerResponse,
} from '../../api';
import {
  Form,
  FormError,
  SelectField,
  SubmitButton,
  TextField,
  fieldErrors,
} from '../../components';
import {
  CADENCE_LABELS,
  CONTRACT_DEFAULTS,
  KIND_LABELS,
  ONBOARDING_COPY,
  REFERENCE_DATE_LABELS,
  REFERENCE_DATE_MEANINGS,
  RULE_LABELS,
  RULE_MEANINGS,
  optionsWithDefault,
  validateContractForm,
  type ContractFormErrors,
  type ContractFormState,
} from './onboarding';

/**
 * The one form both creating and editing a contract go through.
 *
 * One component rather than two because the fields are the same fields and the
 * only thing that differs is what happens to them on submit -- a create sends
 * all of them, an edit sends the subset that moved. Two forms would be two
 * places to forget a field the day the contract grows one.
 *
 * **The company is not a field.** `POST /contracts` resolves it from the
 * `X-Company-Id` scope header, so there is nothing to choose here and nothing
 * that could disagree with the company whose detail view this form is inside.
 *
 * **The two cycle terms show their defaults rather than applying them
 * silently.** `reference_date_rule` and `off_cycle_rule` are terms of the
 * insurer's contract, and `off_cycle_rule` defaults to "monthly file only"
 * precisely because assuming otherwise is the dangerous direction. A default
 * that is applied by omission is an agreement nobody saw; these are on screen,
 * marked as the API's default, with a sentence saying what the chosen value
 * means.
 */
export function ContractForm({
  insurers,
  value,
  onChange,
  onSubmit,
  error,
  pending,
  submitLabel,
  pendingLabel,
  ariaLabel,
  secondaryAction,
  localError,
}: {
  insurers: readonly InsurerResponse[];
  value: ContractFormState;
  onChange: (next: ContractFormState) => void;
  /** Called only once the browser-side checks pass. */
  onSubmit: () => void;
  /** Whatever the server said about the last attempt. */
  error: unknown;
  pending: boolean;
  submitLabel: string;
  pendingLabel: string;
  ariaLabel: string;
  secondaryAction?: React.ReactNode;
  /** A refusal the screen itself made, such as "nothing changed". */
  localError?: string | undefined;
}) {
  const [touched, setTouched] = useState<ContractFormErrors>({});
  const serverErrors = fieldErrors(error);

  const submit = () => {
    const problems = validateContractForm(value);
    setTouched(problems);
    if (Object.keys(problems).length > 0) {
      return;
    }
    onSubmit();
  };

  const errorFor = (field: string): string | undefined => touched[field] ?? serverErrors[field];

  return (
    <Form onSubmit={submit} aria-label={ariaLabel}>
      <FormError error={error} />
      {localError !== undefined && (
        <p className="field__error" role="alert">
          {localError}
        </p>
      )}

      <SelectField
        label={ONBOARDING_COPY.insurerLabel}
        value={value.insurerId}
        onChange={(insurerId) => onChange({ ...value, insurerId })}
        options={insurers.map((insurer) => ({
          value: insurer.id,
          label: `${insurer.code} — ${insurer.name}`,
        }))}
        placeholder={ONBOARDING_COPY.insurerPlaceholder}
        hint={ONBOARDING_COPY.insurerHint}
        required
        {...(errorFor('insurer_id') !== undefined && { error: errorFor('insurer_id') })}
      />

      <SelectField
        label={ONBOARDING_COPY.kindLabel}
        value={value.kind}
        onChange={(kind) =>
          onChange({ ...value, kind: (kind || CONTRACT_DEFAULTS.kind) as ContractKind })
        }
        options={(Object.keys(KIND_LABELS) as ContractKind[]).map((kind) => ({
          value: kind,
          label: KIND_LABELS[kind],
        }))}
        hint={ONBOARDING_COPY.kindHint}
        {...(errorFor('kind') !== undefined && { error: errorFor('kind') })}
      />

      <TextField
        label={ONBOARDING_COPY.subestipulanteLabel}
        value={value.subestipulante}
        onChange={(subestipulante) => onChange({ ...value, subestipulante })}
        hint={ONBOARDING_COPY.subestipulanteHint}
        inputMode="numeric"
        required
        {...(errorFor('subestipulante') !== undefined && { error: errorFor('subestipulante') })}
      />

      <TextField
        label={ONBOARDING_COPY.moduloLabel}
        value={value.modulo}
        onChange={(modulo) => onChange({ ...value, modulo })}
        hint={ONBOARDING_COPY.moduloHint}
        inputMode="numeric"
        required
        {...(errorFor('modulo') !== undefined && { error: errorFor('modulo') })}
      />

      <p className="panel__footnote">{ONBOARDING_COPY.policyReferencesNote}</p>

      <TextField
        label={ONBOARDING_COPY.insurerContractNumberLabel}
        value={value.insurerContractNumber}
        onChange={(insurerContractNumber) => onChange({ ...value, insurerContractNumber })}
        hint={ONBOARDING_COPY.insurerContractNumberHint}
        inputMode="numeric"
        {...(errorFor('insurer_contract_number') !== undefined && {
          error: errorFor('insurer_contract_number'),
        })}
      />

      <TextField
        label={ONBOARDING_COPY.apoliceLabel}
        value={value.apolice}
        onChange={(apolice) => onChange({ ...value, apolice })}
        hint={ONBOARDING_COPY.apoliceHint}
        inputMode="numeric"
        {...(errorFor('apolice') !== undefined && { error: errorFor('apolice') })}
      />

      <SelectField
        label={ONBOARDING_COPY.cadenceLabel}
        value={value.cadence}
        onChange={(cadence) =>
          onChange({
            ...value,
            cadence: (cadence || CONTRACT_DEFAULTS.submission_cadence) as SubmissionCadence,
          })
        }
        options={optionsWithDefault(CADENCE_LABELS, CONTRACT_DEFAULTS.submission_cadence)}
        hint={ONBOARDING_COPY.cadenceHint}
        {...(errorFor('submission_cadence') !== undefined && {
          error: errorFor('submission_cadence'),
        })}
      />

      <TextField
        label={ONBOARDING_COPY.cutoffDayLabel}
        value={value.cutoffDay}
        onChange={(cutoffDay) => onChange({ ...value, cutoffDay })}
        hint={ONBOARDING_COPY.cutoffDayHint}
        inputMode="numeric"
        required
        {...(errorFor('cutoff_day') !== undefined && { error: errorFor('cutoff_day') })}
      />

      <p className="panel__footnote">{ONBOARDING_COPY.termsNote}</p>

      <SelectField
        label={ONBOARDING_COPY.referenceDateLabel}
        value={value.referenceDateRule}
        onChange={(rule) =>
          onChange({
            ...value,
            referenceDateRule: (rule || CONTRACT_DEFAULTS.reference_date_rule) as ReferenceDateRule,
          })
        }
        options={optionsWithDefault(REFERENCE_DATE_LABELS, CONTRACT_DEFAULTS.reference_date_rule)}
        hint={REFERENCE_DATE_MEANINGS[value.referenceDateRule]}
        {...(errorFor('reference_date_rule') !== undefined && {
          error: errorFor('reference_date_rule'),
        })}
      />

      <SelectField
        label={ONBOARDING_COPY.offCycleLabel}
        value={value.offCycleRule}
        onChange={(rule) =>
          onChange({
            ...value,
            offCycleRule: (rule || CONTRACT_DEFAULTS.off_cycle_rule) as OffCycleRule,
          })
        }
        options={optionsWithDefault(RULE_LABELS, CONTRACT_DEFAULTS.off_cycle_rule)}
        hint={RULE_MEANINGS[value.offCycleRule]}
        {...(errorFor('off_cycle_rule') !== undefined && { error: errorFor('off_cycle_rule') })}
      />

      <div className="form__actions">
        <SubmitButton pending={pending} pendingLabel={pendingLabel}>
          {submitLabel}
        </SubmitButton>
        {secondaryAction}
      </div>
    </Form>
  );
}
